/**
 * ChatModelAdapter that calls our server-side chat endpoint.
 * The server runs the Anthropic tool-use loop and streams results back via SSE.
 */
import type { ChatModelAdapter, ChatModelRunResult } from '@assistant-ui/react';
import type { ClientCitation } from '@upright/common/chat/citation-types';
import { refreshAccessToken } from '../api/common/fetcher-core';
import { removeTokenFromStorageAndForceNewLogin } from '../api/iam/access/utils';
import { getServerBaseUrl } from '../untidy/server-base-url';
import {
  DEFAULT_CHAT_DEBUG_MODEL,
  DEFAULT_CHAT_DEBUG_PROVIDER,
  loadChatDebugModel,
  loadChatDebugProvider,
  loadChatDebugReasoningEnabled,
  loadChatInternalMode,
  loadChatUserInstructions,
  MAX_CHAT_USER_INSTRUCTIONS_LENGTH,
} from '../common/generic/utilities/storage';
import { registerCitation } from './citation-registry';
import { registerGeneratedImage } from './generated-image-registry';
import { getStickyPageChatBinding } from './current-page-chat-binding';
import { chatLensSignalForPathContext, getStickyPageChatPathContext } from './current-page-chat-path-context';
import { clearToolProgress, setToolProgress } from './tool-progress-registry';
import {
  appendChatDebugEntry,
  composerState,
  hiddenEntityContext,
  makeErrorContent,
  makeTokenLimitContent,
  pageChatContext,
  stripLargeToolData,
  type ToolResultWithUi,
  webSearchFieldsForNextRequest,
} from './upright-chat-shared';
import { autoReattachEnabled, selectOutgoingAttachments, type ChatAttachmentPayload } from './reattach-attachments';
import { buildUploadErrorMessage } from './upload-error-message';
import { logAttachmentUploadError } from './attachment-validation';

/**
 * PLT-4503 / PLT-5003: detect the AI-chat token-limit 429 by its JSON Boom
 * error body `{ errorCode: 'AI_CHAT_TOKEN_LIMIT_REACHED' }`. When it matches,
 * the caller renders the dedicated `ChatTokenLimitCard` (a friendly
 * "you've reached your usage limit" card with a tier-specific CTA) instead of
 * the generic red error block.
 *
 * Defensive: any parse / shape mismatch returns `false`. We never want a
 * malformed error body to mask the underlying 429 — better to fall back to
 * the generic message than crash the chat surface.
 */
async function isAiChatTokenLimitResponse(response: Response): Promise<boolean> {
  if (response.status !== 429) return false;
  try {
    const body = (await response.clone().json()) as {
      errorCode?: string;
    };
    return body.errorCode === 'AI_CHAT_TOKEN_LIMIT_REACHED';
  } catch {
    return false;
  }
}

/**
 * Render the `[Context: ...]` line prepended to the first user message.
 *
 * Always emits `entityId` and `entityType` first (the fields that have been
 * shipped since PLT-4494 and that the backend matchers already key on), then
 * any extra fields in object-key order. Pure string concatenation — values
 * are emitted verbatim because they originate in code (deep-link call sites)
 * rather than user input, and the model reads the line as plain text. The
 * entity-only and entity-plus-extras forms differ only in the trailing
 * fields, so older readers that only look for entityId / entityType keep
 * working unchanged.
 */
function buildHiddenContextLine(ctx: { entityId: string; entityType: string; extra?: Record<string, string> }): string {
  return formatHiddenContextLine({ entityId: ctx.entityId, entityType: ctx.entityType, ...(ctx.extra ?? {}) });
}

/**
 * The `k=v, k=v` formatting of the line, shared by the entity-bound form above
 * and the entity-less form (page fields only). Insertion order is preserved, so
 * callers control field order and a fixed source keeps producing a byte-stable
 * line — see the prompt-cache note at the call site.
 */
function formatHiddenContextLine(fields: Record<string, string>): string {
  const parts = Object.entries(fields).map(([k, v]) => `${k}=${v}`);
  return `[Context: ${parts.join(', ')}]`;
}

/**
 * PLT-4494 helper: prepend a hidden context line to the first text part of a
 * user message's content. Both string-shaped and array-shaped content are
 * supported (assistant-ui uses the array shape with `text` parts; the
 * primitive-string shape is the legacy fallback). If the array contains no
 * text part (e.g. an attachment-only message — currently impossible at the
 * call site but defended against in case the shape evolves), we splice in a
 * synthetic text part at index 0.
 */
function prependHiddenContext(content: string | readonly any[] | any[], contextLine: string): string | any[] {
  if (typeof content === 'string') {
    return `${contextLine}\n\n${content}`;
  }
  const firstTextIdx = content.findIndex((p: any) => p?.type === 'text');
  if (firstTextIdx === -1) {
    return [{ type: 'text', text: contextLine }, ...content];
  }
  const cloned: any[] = [...content];
  const textPart = cloned[firstTextIdx];
  cloned[firstTextIdx] = {
    ...textPart,
    text: `${contextLine}\n\n${textPart.text ?? ''}`,
  };
  return cloned;
}

export const uprightChatAdapter: ChatModelAdapter = {
  async *run({ messages, abortSignal }) {
    // PLT-4720: best-effort pre-flight refresh of the `up-access` cookie
    // before we open a long-lived SSE response. Auth now travels as the
    // HttpOnly cookie via `credentials: 'include'` below; we no longer
    // attach the JWT to the `Authorization` header. Silently swallow a
    // failed refresh — the chat request itself will surface the 401 if
    // the user truly has no session.
    try {
      await refreshAccessToken();
    } catch {
      // Ignore — the chat POST below will fail and surface the error.
    }

    // PLT-7271: waits until the user's provider is known, so an EU chat opened
    // from a link does not send its first message with the default provider's
    // web search setting. The backend checks the provider named here against
    // the one it resolves.
    const webSearchFields = await webSearchFieldsForNextRequest();

    // Extract attachments (base64 data) from the last user message
    const lastUserMsg = [...messages].reverse().find(m => m.role === 'user');
    const attachments =
      lastUserMsg?.attachments
        .map((att: any) => {
          const filePart = att.content?.find((p: any) => p.type === 'file');
          if (!filePart) return null;
          return {
            data: filePart.data, // base64 content
            filename: filePart.filename || att.name,
            mimeType: filePart.mimeType || att.contentType || 'application/octet-stream',
          };
        })
        .filter(Boolean) ?? [];

    // Capture logging state for this message before resetting pendingSource
    const currentSource = composerState.pendingSource;
    const currentMessageIndex = composerState.messageIndex;
    const currentThreadId = composerState.threadId;
    // Reset source back to default for subsequent typed messages
    composerState.pendingSource = 'user_input';
    // Increment message index for the next user message
    composerState.messageIndex++;

    // PLT-5741: stamp every yielded assistant message with the coordinates it
    // is persisted under — `(threadId, messageIndex)` — so the per-message
    // feedback control knows which `chat_message_feedback` row to target. We
    // reuse `messageMetadata` on all yields; assistant-ui merges `metadata.custom`
    // across a run's yields, so the value survives to the final message. The
    // reload path stamps the same shape in `history/seed-message.ts`.
    const messageMetadata = {
      custom: { threadId: currentThreadId, messageIndex: currentMessageIndex },
    };

    // PLT-5558: auto-re-attach a previously-uploaded SMALL file on follow-up
    // turns. The server discards each upload after its turn, so without this a
    // user prompted to fix a formatting issue would have to re-upload the file.
    // Only files below MAX_AUTO_REATTACH_BYTES are remembered/replayed — large
    // holdings CSVs would re-upload and re-tokenize at full input cost every
    // turn. The remember/replay decision lives in `selectOutgoingAttachments`.
    // PLT-5607: gated on the fund-upload entitlement (`autoReattachEnabled`) —
    // for everyone else it passes this turn's uploads through unchanged, exactly
    // as chat behaved before PLT-5558.
    const uploadedAttachments = attachments.filter((a): a is ChatAttachmentPayload => a !== null);
    const outgoingAttachments = selectOutgoingAttachments(
      uploadedAttachments,
      currentThreadId,
      autoReattachEnabled.value,
    );

    // Custom user instructions (PLT-4492) — team-only, set from the chat
    // debug panel and persisted to localStorage. Read fresh on every send so
    // an in-flight edit takes effect on the next message without a page
    // reload. Trimmed and capped client-side; the backend re-validates and
    // refuses non-team users via `requireTeamUser`. We omit the field rather
    // than sending `""` so an empty value never broadens the request shape.
    const userInstructionsRaw = loadChatUserInstructions().trim();
    const userInstructions =
      userInstructionsRaw.length > 0 ? userInstructionsRaw.slice(0, MAX_CHAT_USER_INSTRUCTIONS_LENGTH) : undefined;

    // PLT-4507: team-only model + reasoning overrides from the debug panel.
    // Same pattern as `userInstructions` — read fresh per send (so a
    // panel toggle takes effect on the next message), and only included
    // in the payload when they differ from the defaults so a non-team
    // browser session has identical wire shape to before. The backend
    // re-checks `isTeamUser` and silently drops these for non-team
    // callers, so a copy-pasted team browser session can't grant access
    // to a non-team user via the JWT alone.
    const debugModel = loadChatDebugModel();
    const modelOverride = debugModel !== DEFAULT_CHAT_DEBUG_MODEL ? debugModel : undefined;
    const reasoningEnabledStored = loadChatDebugReasoningEnabled();
    const reasoningOverride = reasoningEnabledStored ? undefined : false;

    // PLT-5035: the staff EU testing switch from the debug panel. Same pattern
    // as the model / reasoning overrides — read fresh per send and only
    // included in the payload when it differs from the default provider, so a
    // browser session that never touched the toggle keeps the identical wire
    // shape. The backend honours the field for team users only; a non-team
    // user's chat is routed by their `USE_AI_CHAT_EU_RESIDENCY` grant alone
    // (PLT-7018), so forcing this field changes nothing for them.
    const debugProvider = loadChatDebugProvider();
    const providerOverride = debugProvider !== DEFAULT_CHAT_DEBUG_PROVIDER ? debugProvider : undefined;

    // PLT-5819: profiling opt-in. Read fresh per send (mirrors the model /
    // reasoning / provider overrides) straight from localStorage — the adapter
    // runs outside React so it can't use `useIsChatInternalMode`, but the toggle
    // is only ever written by team users and the backend re-gates on
    // `isTeamUser`, so a stale flag on a non-team account is a server-side
    // no-op. When chat internal mode is on we ask the backend to enable the
    // per-round chat profiler (surfaced in the debug panel) and the logs-only
    // search profiler for this turn. Field omitted when off, so non-internal
    // turns keep the identical wire shape as before.
    const profileEnabled = loadChatInternalMode();

    // Hidden entity context (PLT-4494; extras added in PLT-4523; in-page
    // panel seeding since PLT-4676): when the chat was opened via a deep
    // link carrying `?entityId=...&entityType=...`, or via the side panel
    // through `openChatPanel({ entityId, entityType, extra? })`, prepend a
    // single context line to the FIRST user message before serialization
    // so the backend / model can resolve the entity and pin the answer to
    // a specific row. Only the serialized payload is mutated —
    // assistant-ui's stored messages are untouched, so the user's bubble
    // continues to show only the visible prompt they clicked into. We
    // re-apply on every turn so subsequent messages also carry the
    // context (the runtime replays the full history on each `run`).
    //
    // Extra fields (e.g. matterId, valence, driver, intent) from the
    // panel call site are emitted verbatim as `k=v` after the entity
    // binding. We don't escape values — they originate in code, not in
    // user input, and the model treats the line as plain text. Keys are
    // emitted in insertion order so prompt-cache hits stay stable for a
    // fixed source.
    //
    // PLT-4625 follow-up: the side-panel's visible-chip URL (from
    // `pageChatContext.value.href`) is folded into the same hidden
    // context line as `pageUrl=…`. The model gets a richer hint about
    // the user's current view (lens, dashboard tab, etc.) than the
    // entityId / entityType binding alone provides. The chip's label
    // (e.g. "Neste — Net impact") is intentionally NOT included — it's
    // UI copy, not signal — but the URL is. (The lens does travel, as of
    // PLT-6779 below, but as a normalised `lens=` field rather than as
    // the chip's display string.) Both pieces are optional
    // and combine into a single `[Context: …]` line so we don't pay a
    // second prefix on every turn. When there's no entity binding but
    // there IS a page context (e.g. global Ask-AI open from a company
    // page), the line is still emitted with just `pageUrl=…`.
    //
    // PLT-6779: the lens the user is looking at goes on the same line as
    // `lens=<name>`, so the model can route a framework-less question to the
    // lens in front of the user instead of to the org's default framework
    // (`resolveDefaultFramework` server-side). Two things make this a separate
    // field rather than something read out of `pageUrl`: the model should not
    // have to parse a URL to learn a fact we already hold as an enum, and the
    // name we send is the one the prompt's own routing table uses
    // (`chatLensSignalForPathContext`). It is also the one page field that is
    // NOT frozen at panel-open — the sticky path store follows navigation while
    // the panel is open, so switching lens mid-conversation changes the line on
    // the next turn (and re-primes the message cache, which is the correct
    // trade: a deliberate navigation is exactly when the steer should move).
    const hiddenContext = hiddenEntityContext.value;
    const pageContext = pageChatContext.value;
    const lensSignal = chatLensSignalForPathContext(getStickyPageChatPathContext());
    // Ordered: pageUrl then lens, and both optional. Shared by the two branches
    // below so the entity-bound and entity-less forms cannot drift.
    const pageFields: Record<string, string> = {};
    if (pageContext?.href) pageFields.pageUrl = pageContext.href;
    if (lensSignal) pageFields.lens = lensSignal;
    const hasPageFields = Object.keys(pageFields).length > 0;
    let hiddenContextLine: string | null = null;
    if (hiddenContext) {
      // Merge the page fields into the extras so they ride on the same line as
      // the entity binding via the shared builder.
      const extraWithPage: Record<string, string> | undefined = hasPageFields
        ? { ...(hiddenContext.extra ?? {}), ...pageFields }
        : hiddenContext.extra;
      hiddenContextLine = buildHiddenContextLine({ ...hiddenContext, extra: extraWithPage });
    } else if (hasPageFields) {
      hiddenContextLine = formatHiddenContextLine(pageFields);
    }
    const firstUserIndex = messages.findIndex(m => m.role === 'user');
    const serializedMessages = messages.map((m, idx) => {
      const baseContent = Array.isArray(m.content) ? stripLargeToolData(m.content) : m.content;
      if (hiddenContextLine !== null && idx === firstUserIndex) {
        return {
          role: m.role,
          content: prependHiddenContext(baseContent, hiddenContextLine),
        };
      }
      return { role: m.role, content: baseContent };
    });

    // PLT-4623: build the chat URL from `getServerBaseUrl()` rather than using
    // a same-origin relative path. ui-preview deployments live on
    // `*.uprightdb.com` (S3 + CloudFront, no ALB origin) — a relative `/api/chat`
    // there hits S3, S3 404s, CloudFront's error-response config rewrites to
    // `/index.html`, and the streaming fetch reads back HTML. The rest of the
    // client already goes cross-origin via `getServerBaseUrl()`; chat now
    // follows the same convention. The corresponding CORS headers are written
    // explicitly in the SSE handler (`upserve/modules/chat/index.ts`) because
    // it bypasses Hapi's CORS middleware (raw response).
    const chatUrl = `${getServerBaseUrl()}/api/chat`;

    // PLT-5041 / PLT-5235: when the conversation is bound to a company, send
    // that company id. The backend only consumes it for DMA Consultant orgs,
    // where it attributes / caps token usage per client company; every other
    // org type ignores it. Non-company bindings (group / portfolio) and
    // no-binding turns send nothing — single-company consultants are resolved
    // server-side from their sole accessible company.
    //
    // Two carriers, checked in order: the STICKY page binding (set by the
    // company page / the chip, and surviving navigation from the company page
    // into full `/chat` mid-conversation) and the hidden entity context (set
    // from a `/chat?entityId=…&entityType=company` deep-link). The fallback to
    // the hidden context decouples attribution from the visible chip: a
    // company-scoped deep-link with no `entityLabel` publishes no chip, so the
    // sticky slot stays empty, but the turn must still be metered to that
    // company. Without this, consultant token routing would silently depend on
    // a chip label being present.
    const stickyBinding = getStickyPageChatBinding();
    const boundCompanyId =
      stickyBinding?.entityType === 'company'
        ? stickyBinding.entityId
        : hiddenContext?.entityType === 'company'
          ? hiddenContext.entityId
          : null;

    let response: Response;
    try {
      response = await fetch(chatUrl, {
        method: 'POST',
        // PLT-4720: cookie-based auth — send the `up-access` HttpOnly
        // cookie. The SSE handler (`upserve/modules/chat/index.ts`)
        // writes CORS headers manually for the raw-response path, so
        // cross-origin previews (`*.uprightdb.com` against the staging
        // backend) keep working.
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: serializedMessages,
          ...(outgoingAttachments.length > 0 ? { attachments: outgoingAttachments } : {}),
          ...webSearchFields,
          ...(userInstructions ? { userInstructions } : {}),
          // PLT-4507: only forward overrides when they differ from the
          // server-side defaults — keeps the wire shape minimal for
          // non-debug-panel users.
          ...(modelOverride ? { model: modelOverride } : {}),
          ...(reasoningOverride === false ? { reasoningEnabled: false } : {}),
          ...(providerOverride ? { provider: providerOverride } : {}),
          // PLT-5819: auto-on profiling whenever chat internal mode is on.
          ...(profileEnabled ? { profile: true } : {}),
          threadId: currentThreadId,
          messageIndex: currentMessageIndex,
          source: currentSource,
          // PLT-5041: client-company attribution for DMA Consultant chat.
          ...(boundCompanyId ? { companyId: boundCompanyId } : {}),
        }),
        signal: abortSignal,
      });
    } catch (err: any) {
      // Network error or aborted — surface inline instead of crashing React
      if (err.name === 'AbortError') return;
      yield {
        content: makeErrorContent('Connection error. Please check your internet and try again.'),
        metadata: messageMetadata,
      };
      return;
    }

    if (response.status === 401) {
      removeTokenFromStorageAndForceNewLogin();
      throw new Error('Session expired');
    }

    if (!response.ok) {
      // PLT-4503 / PLT-5003: when the org has reached its AI-chat token cap,
      // the backend returns 429 with a structured `errorCode:
      // 'AI_CHAT_TOKEN_LIMIT_REACHED'` payload. Render the dedicated usage-limit
      // card (tier-specific CTA, no number shown) rather than a raw error.
      if (await isAiChatTokenLimitResponse(response)) {
        // PLT-5266: pass the bound client company so the card can offer a
        // consultant admin a self-serve Starter→Pro upgrade for that company.
        yield { content: makeTokenLimitContent(boundCompanyId), metadata: messageMetadata };
        return;
      }
      const message =
        response.status === 429
          ? 'Too many requests. Please wait a moment and try again.'
          : response.status >= 500
            ? 'The server encountered an error. Please try again.'
            : 'The request could not be completed. Please try again.';
      yield { content: makeErrorContent(message), metadata: messageMetadata };
      return;
    }

    const contentType = response.headers.get('content-type') || '';

    // SSE streaming path
    if (contentType.includes('text/event-stream')) {
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      // Cancel the reader when abort is signalled (e.g. user clicks stop).
      // The fetch signal only aborts the initial request; once the response
      // body stream is open we need to cancel the reader explicitly.
      const onAbort = () => {
        void reader.cancel();
      };
      abortSignal.addEventListener('abort', onAbort);
      // Content parts in stream order so text stays in place when tool calls arrive
      const contentParts: Array<
        | { kind: 'text'; text: string }
        | {
            kind: 'tool-call';
            toolCallId: string;
            toolName: string;
            args: Record<string, unknown>;
            result?: ToolResultWithUi;
          }
      > = [];
      let currentTextIdx = -1;

      // Map a server-side reasoning `blockIndex` (resets to 0 each
      // assistant API call) to the unique `toolCallId` we synthesised
      // for that block. The toolCallId must be unique across the whole
      // thread — assistant-ui's `tapResources` keys content parts by
      // toolCallId at thread scope, so a bare `reasoning-${blockIndex}`
      // collides with the same-indexed reasoning block from a previous
      // assistant turn (a clarifying question + an answer is enough to
      // trigger it). See PLT-4452: "Duplicate key toolCallId-reasoning-0
      // in tapResources" warning + the React rendering breakage that
      // follows.
      const reasoningToolCallIds = new Map<number, string>();

      // Helper: build the content snapshot from contentParts for yielding to React.
      const buildContent = (): ChatModelRunResult['content'] => {
        const parts: Array<
          | { type: 'text'; text: string }
          | { type: 'tool-call'; toolCallId: string; toolName: string; args: Record<string, unknown>; result?: unknown }
        > = [];
        for (const part of contentParts) {
          if (part.kind === 'text') {
            parts.push({ type: 'text', text: part.text });
          } else {
            parts.push({
              type: 'tool-call',
              toolCallId: part.toolCallId,
              toolName: part.toolName,
              args: part.args,
              result: part.result,
            });
          }
        }
        return parts as ChatModelRunResult['content'];
      };

      // Parse SSE lines from the buffer into discrete events.
      // Returns an array so we can yield between events when needed
      // (e.g. to show streaming preview before the final tool-result arrives).
      const parseSseEvents = (lines: string[]) => {
        const events: Array<Record<string, any>> = [];
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') break;
          try {
            events.push(JSON.parse(data));
          } catch {
            // skip malformed JSON
          }
        }
        return events;
      };

      try {
        while (true) {
          if (abortSignal.aborted) break;
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          const events = parseSseEvents(lines);

          for (const event of events) {
            if (event.type === 'reasoning-start') {
              // Adaptive thinking summary block — render as a synthetic
              // `reasoning_summary` tool-call content part. Same trick
              // as `chat_error`: piggyback on assistant-ui's tool-UI
              // mechanism so the "Reasoning" affordance lives inline
              // with the message without a parallel rendering tree.
              //
              // The toolCallId must be unique across the whole thread,
              // not just within this stream — `reasoningBlockIndex`
              // (server-assigned content_block index) resets to 0 each
              // assistant API call, so two consecutive turns each emit
              // a `reasoning-0` and collide at the React-key level. We
              // mint a stream-unique id and remember it in
              // `reasoningToolCallIds` so subsequent `reasoning-delta`
              // / `reasoning-stop` events for the same block find the
              // right content part.
              currentTextIdx = -1;
              const blockIndex = event.blockIndex as number;
              const redacted = event.redacted === true;
              const toolCallId = `reasoning-${Date.now()}-${blockIndex}-${Math.random().toString(36).slice(2, 8)}`;
              reasoningToolCallIds.set(blockIndex, toolCallId);
              contentParts.push({
                kind: 'tool-call',
                toolCallId,
                toolName: 'reasoning_summary',
                args: { blockIndex, redacted, text: '' },
                // No `result` yet — the block is still streaming. The
                // final result is set on `reasoning-stop` so the UI can
                // detect "block finished streaming".
              });
            } else if (event.type === 'reasoning-delta') {
              const blockIndex = event.blockIndex as number;
              const toolCallId = reasoningToolCallIds.get(blockIndex);
              const idx =
                toolCallId !== undefined
                  ? contentParts.findIndex(p => p.kind === 'tool-call' && p.toolCallId === toolCallId)
                  : -1;
              if (idx >= 0) {
                const old = contentParts[idx] as Extract<(typeof contentParts)[number], { kind: 'tool-call' }>;
                contentParts[idx] = {
                  ...old,
                  args: {
                    ...old.args,
                    text: ((old.args.text as string | undefined) ?? '') + String(event.text ?? ''),
                  },
                };
              }
              // No yield-immediate here: text-delta yields below cover
              // the same render pass. Reasoning deltas don't need their
              // own macrotask boundary.
            } else if (event.type === 'reasoning-stop') {
              const blockIndex = event.blockIndex as number;
              const toolCallId = reasoningToolCallIds.get(blockIndex);
              const idx =
                toolCallId !== undefined
                  ? contentParts.findIndex(p => p.kind === 'tool-call' && p.toolCallId === toolCallId)
                  : -1;
              if (idx >= 0) {
                const old = contentParts[idx] as Extract<(typeof contentParts)[number], { kind: 'tool-call' }>;
                contentParts[idx] = { ...old, result: { data: { complete: true } } };
              }
              currentTextIdx = -1;
            } else if (event.type === 'text-delta') {
              if (currentTextIdx >= 0) {
                (contentParts[currentTextIdx] as { kind: 'text'; text: string }).text += event.text;
              } else {
                currentTextIdx = contentParts.length;
                contentParts.push({ kind: 'text', text: event.text });
              }
            } else if (event.type === 'citation-meta') {
              // Per-citation metadata. Pair with the inline `<cite id="<id>"/>`
              // marker that the server interleaved into the text-delta stream
              // (see `stream-events.ts`'s `citations_delta` branch). Writing
              // to the module-level registry now ensures it's populated by
              // the time the next React yield re-renders the markdown — the
              // remark plugin resolves the marker by id at that point.
              const id = event.id as string | undefined;
              const index = event.index as number | undefined;
              const citation = event.citation as ClientCitation | undefined;
              if (typeof id === 'string' && typeof index === 'number' && citation) {
                registerCitation(id, { citation, index });
              }
              // Intentionally NOT setting currentTextIdx = -1 here. Citations
              // are interleaved INSIDE a text block (mid-paragraph), not at
              // a block boundary — subsequent text-delta events should keep
              // appending to the current text part, not start a new one.
            } else if (event.type === 'text-block-citations') {
              // Legacy per-block flush retained for the AI test workbench's
              // [citations] transcript section. The chat UI itself now uses
              // the per-citation `citation-meta` events above.
            } else if (event.type === 'upload-error') {
              // Non-terminal upload problem — surface as an inline error card.
              // The stream continues afterwards (this is distinct from the
              // terminal `error` event below). We render via the same synthetic
              // `chat_error` tool-call surface used elsewhere so the styling
              // stays consistent and we don't have to register a second tool UI.
              // The card the user reads no longer carries the raw MIME type
              // (PLT-6061), so log the event's own fields — a customer's browser
              // console is where support realistically reads this back.
              logAttachmentUploadError('server-upload-error', event);
              const message = buildUploadErrorMessage(event);
              currentTextIdx = -1;
              contentParts.push({
                kind: 'tool-call',
                // This part is replayed to the model as a tool_use block, whose id
                // must match `^[a-zA-Z0-9_-]+$` (PLT-7738), so keep the filename
                // and reason out of it. The random suffix keeps several upload
                // errors from the same event batch distinct.
                toolCallId: `upload-err-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                toolName: 'chat_error',
                args: { message },
                result: { data: null },
              });
            } else if (event.type === 'context-warning' || event.type === 'context-trimmed') {
              // PLT-5349: non-terminal conversation-length notices. `context-warning`
              // fires when the conversation is getting long (the server already
              // emitted this event but the client previously dropped it, so the
              // warning never reached the user); `context-trimmed` fires once when
              // the sliding window actually drops older messages. Render both as an
              // informational notice card — the stream continues afterwards. Reuse
              // the synthetic `chat_notice` tool surface so styling stays consistent.
              const message = typeof event.message === 'string' ? event.message : undefined;
              if (message) {
                currentTextIdx = -1;
                contentParts.push({
                  kind: 'tool-call',
                  toolCallId: `${event.type}-${Date.now()}`,
                  toolName: 'chat_notice',
                  args: { message },
                  result: { data: null },
                });
              }
            } else if (event.type === 'tool-call') {
              currentTextIdx = -1;
              contentParts.push({
                kind: 'tool-call',
                toolCallId: event.toolCallId,
                toolName: event.toolName,
                args: event.args,
              });
            } else if (event.type === 'tool-call-args') {
              // PLT-5027: a `server_tool_use` block's input (e.g. the executed
              // Python for code_execution) is empty on the initial `tool-call`
              // event and only becomes available once it has finished
              // streaming. Merge the completed args into the existing
              // tool-call content part (matched by id) so the code-execution
              // UI can render `args.code`. Replace the object so assistant-ui's
              // shallow compare picks up the change.
              const argsIdx = contentParts.findIndex(p => p.kind === 'tool-call' && p.toolCallId === event.toolCallId);
              if (argsIdx >= 0) {
                const old = contentParts[argsIdx] as Extract<(typeof contentParts)[number], { kind: 'tool-call' }>;
                contentParts[argsIdx] = {
                  ...old,
                  args: { ...old.args, ...event.args },
                };
              }
            } else if (event.type === 'status') {
              // PLT-5031: coarse mid-tool progress for long-running tools (e.g.
              // physical climate risk). When scoped to a `toolCallId`, surface it
              // on that tool's card via the module-level progress registry. The
              // legacy un-scoped status (e.g. "Retrying…") carries no toolCallId
              // and is ignored here — it predates this channel.
              if (typeof event.toolCallId === 'string' && typeof event.message === 'string') {
                setToolProgress(event.toolCallId, event.message);
              }
            } else if (event.type === 'tool-result') {
              // The tool finished — drop any in-progress status for it (PLT-5031).
              if (typeof event.toolCallId === 'string') clearToolProgress(event.toolCallId);
              const tc = contentParts.find(
                (p): p is Extract<(typeof contentParts)[number], { kind: 'tool-call' }> =>
                  p.kind === 'tool-call' && p.toolCallId === event.toolCallId,
              );
              if (tc) {
                // If this tool was streaming, give React one more macrotask to
                // render the streaming panel before we overwrite with the final result.
                if (tc.result && (tc.result as any)._partial) {
                  yield { content: buildContent(), metadata: messageMetadata };
                  await new Promise(r => setTimeout(r, 50));
                }
                tc.result = {
                  data: event.result,
                  ...(event.ui ? { _ui: event.ui } : {}),
                };
              }
            } else if (event.type === 'tool-result-files') {
              // PLT-5030: images the code-execution sandbox generated (e.g. a
              // matplotlib PNG), downloaded server-side and delivered as base64
              // data URLs. Sent as a separate event from `tool-result` (it lands
              // just after it) so the megabytes of base64 never hit the
              // persisted transcript. Merge into the existing tool-call's
              // `result.data.images` so `CodeExecutionToolUI` can render them.
              // Replace the result object so assistant-ui's shallow compare
              // re-renders.
              const tc = contentParts.find(
                (p): p is Extract<(typeof contentParts)[number], { kind: 'tool-call' }> =>
                  p.kind === 'tool-call' && p.toolCallId === event.toolCallId,
              );
              if (tc && Array.isArray(event.images)) {
                const prev = tc.result;
                const prevData = (prev?.data ?? {}) as Record<string, unknown>;
                tc.result = {
                  ...(prev ?? {}),
                  data: { ...prevData, images: event.images },
                };
                // PLT-5148: also index each delivered image by filename so the
                // markdown `img` override can resolve the model's inline
                // `![chart](chart.png)` prose reference to the data URL. Without
                // this the bare filename resolves to a relative URL and 403s.
                for (const img of event.images as Array<{
                  dataUrl?: unknown;
                  mimeType?: unknown;
                  filename?: unknown;
                }>) {
                  if (typeof img.dataUrl === 'string' && typeof img.filename === 'string') {
                    registerGeneratedImage({
                      dataUrl: img.dataUrl,
                      mimeType: typeof img.mimeType === 'string' ? img.mimeType : '',
                      filename: img.filename,
                    });
                  }
                }
              }
            } else if (event.type === 'error') {
              currentTextIdx = -1;
              contentParts.push({
                kind: 'tool-call',
                toolCallId: `err-${Date.now()}`,
                toolName: 'chat_error',
                args: { message: event.message || 'Something went wrong. Please try again.' },
                result: { data: null },
              });
            } else if (event.type === 'usage') {
              // End-of-stream usage payload (PLT-4475). Feed the chat-debug
              // store so the internal-mode debug panel can show per-message
              // tokens / cost / timing. The store is module-level (not
              // Redux) — see `appendChatDebugEntry` for why.
              //
              // We deliberately don't gate on internal-mode-on here: ingesting
              // is cheap, and the panel re-checks both the toggle and team
              // status before rendering. That way, switching internal mode
              // ON mid-thread instantly reveals all the usage entries that
              // arrived while it was off.
              appendChatDebugEntry({
                messageIndex: typeof event.messageIndex === 'number' ? event.messageIndex : currentMessageIndex,
                model: typeof event.model === 'string' ? event.model : 'unknown',
                inputTokens: event.inputTokens ?? 0,
                outputTokens: event.outputTokens ?? 0,
                cacheCreationTokens: event.cacheCreationTokens ?? 0,
                cacheReadTokens: event.cacheReadTokens ?? 0,
                durationMs: event.durationMs ?? 0,
                stopReason: event.stopReason ?? null,
                timeBreakdown: event.timeBreakdown ?? { modelMs: 0, toolMs: 0, otherMs: 0 },
                toolsUsed: Array.isArray(event.toolsUsed) ? event.toolsUsed : [],
                costUsd: event.costUsd ?? null,
                costBreakdownUsd: event.costBreakdownUsd ?? null,
                // PLT-5819: per-round profiling, present only when the backend
                // enabled the profiler for this turn (team + internal mode).
                // Passed through verbatim; the panel renders it defensively.
                ...(Array.isArray(event.roundProfiles) ? { roundProfiles: event.roundProfiles } : {}),
                receivedAt: Date.now(),
              });
            }
          }

          yield { content: buildContent(), metadata: messageMetadata };
        }
      } catch (err: unknown) {
        // reader.read() rejects after reader.cancel(); silently stop.
        if (abortSignal.aborted) return;
        // Instead of crashing the whole app, yield an error card
        const errorMessage = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
        contentParts.push({
          kind: 'tool-call',
          toolCallId: `err-${Date.now()}`,
          toolName: 'chat_error',
          args: { message: errorMessage },
          result: { data: null },
        });
        yield { content: buildContent() };
      } finally {
        abortSignal.removeEventListener('abort', onAbort);
        // No spinner cleanup needed here: the spinner is derived in the UI
        // from `useMessage(s => s.status?.type === 'running')` AND the result
        // being the partial preview (`_partial: true`). When this run
        // terminates — for any reason — assistant-ui flips the message status
        // away from "running" and every spinner in this message stops by
        // derivation. There is no flag to clear, so a future fifth exit path
        // can't re-introduce a stuck spinner. (See option 1 in the discussion
        // on PR #7053.)
      }
    } else {
      // Non-streaming JSON fallback
      const data = await response.json();
      yield {
        content: [{ type: 'text' as const, text: data.text || JSON.stringify(data) }],
        metadata: messageMetadata,
      };
    }
  },
};
